import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { Subject } from 'rxjs';
import { AppDialogBoxResult } from '@ukam/ui-kit/dialog-box/dialog-box.models';
import { AppDialogBoxService } from '@ukam/ui-kit/dialog-box/dialog-box.service';
import { ResultService } from '@ukam/core/services/result.service';
import { CacheService } from '@ukam/core/services/cache.service';
import { Filter, createFilter } from '../models/filter';
import { FilterField } from '@ukam/core/filters/models/filter-field';
import { filterToParams } from '../params/filter.params';
import { FilterApiService } from './filter-api.service';
import { FilterSearchService } from './filter-search.service';
import { FilterSupportService } from './filter-support.service';

export abstract class FilterBaseService {
  private resultService = inject(ResultService);
  private apiService = inject(FilterApiService);
  private searchService = inject(FilterSearchService);
  private supportService = inject(FilterSupportService);
  private cacheService = inject(CacheService);
  private dialogBoxService = inject(AppDialogBoxService);
  private translocoService = inject(TranslocoService);
  private router = inject(Router);

  public working = signal(false);
  public model = signal<Filter | null>(null);
  public fields = signal<any>(null);
  public onCreate$ = new Subject<void>();
  public onSaved$ = new Subject<Filter>();
  readonly fieldsChange = new Subject<FilterField[]>();

  feature?: string;

  constructor(feature?: string) {
    this.cacheService.register(() => this.clear());
    this.supportService.load().subscribe();
    this.feature = feature;
    this.loadFields();
  }

  getById(id: string): void {
    this.working.set(true);
    this.apiService.getById(id).subscribe({
      next: (result) => {
        const model = createFilter(result);
        this.model.set(model);
        this.working.set(false);
        this.fieldsChange.next(model.fields ?? []);
      },
      error: (error) => {
        this.resultService.showError(error);
        this.working.set(false);
        this.fieldsChange.next([]);
      },
    });
  }

  getByFeature(feature: string): void {
    this.working.set(true);
    this.apiService.getByFeature(feature).subscribe({
      next: (result) => {
        const model = createFilter(result);
        this.model.set(model);
        this.working.set(false);
        this.fieldsChange.next(model.fields ?? []);
      },
      error: (error) => {
        this.resultService.showError(error);
        this.working.set(false);
        this.fieldsChange.next([]);
      },
    });
  }

  loadFields() {
    if (!this.feature) {
      this.fields.set(null);
      return;
    }

    this.working.set(true);
    this.apiService.getFields(this.feature).subscribe({
      next: (result) => {
        this.fields.set(result?.fields ?? null);
        this.working.set(false);
      },
      error: (error) => {
        this.resultService.showError(error);
        this.working.set(false);
        throw error;
      },
    });
  }

  create(): void {
    if (this.working()) return;
    this.model.set(createFilter());
    this.onCreate$.next();
  }

  save(showMessage = true): void {
    if (this.working()) return;

    const m = this.model();
    if (!m) return;

    const exists = m.id && m.id.length > 0;

    this.working.set(true);
    const params = filterToParams(m);
    const obs = exists
      ? this.apiService.update(m.id!, params)
      : this.apiService.create(params);
    obs.subscribe({
      next: (result) => {
        const model = createFilter(result);
        this.model.set(model);
        this.searchService.search().subscribe();
        if (showMessage) {
          const msg = exists ? 'Update completed successfully' : 'Registration completed successfully';
          this.resultService.showSuccess(this.translocoService.translate(msg));
        }
        this.working.set(false);
        if (!exists && model.id) {
          this.router.navigate(['/apps/filters', model.id]);
        }
        this.onSaved$.next(model);
      },
      error: (error) => {
        this.resultService.showError(error);
        this.working.set(false);
      },
    });
  }


  _update(): void {
    const m = this.model();
    if (!m) return;

    const exists = m.id && m.id.length > 0;
    const params = filterToParams(m);
    const obs = exists
      ? this.apiService.update(m.id!, params)
      : this.apiService.create(params);
    obs.subscribe({
      next: (result) => {
      },
      error: (error) => {
        this.resultService.showError(error);
        this.working.set(false);
      },
    });
  }

  delete(id: string): void {
    return this._delete([id]);
  }

  deleteMany(): void {
    const ids = this.searchService.getSelectedIds();
    this._delete(ids);
  }

  _delete(ids: Array<string>): void {

    if (!ids || ids?.length == 0) return;

    const _ids = [];
    for (const id of ids) {
      if (id)
        _ids.push(id);
    }

    if (_ids?.length == 0) {
      return;
    };

    const dialogRef = ids?.length > 1 ? this.dialogBoxService.openDeleteMany() : this.dialogBoxService.openDelete();

    dialogRef.afterClosed().subscribe({
      next: (result: AppDialogBoxResult) => {
        if (result?.action !== 'yes') return;
        this.working.set(true);
        this.apiService.batchDelete({ ids: ids }).subscribe({
          next: () => {
            this.searchService.search().subscribe();
            this.resultService.showSuccess(this.translocoService.translate('Delete completed successfully'));
            this.working.set(false);
          },
          error: (error: any) => {
            this.resultService.showError(error);
            this.working.set(false);
          }
        });
      }
    });
  }

  clear(): void {
    this.model.set(null);
  }

  setFields(fields: FilterField[], notify = true): void {
    const model = this.model();
    if (!model) return;

    this.model.set({ ...model, fields });
    this._update();
    if (notify) {
      this.fieldsChange.next(fields);
    }
  }

  setSearchText(value: string | null): void {
    const model = this.model();
    const searchText = value ?? '';
    if (!model || (model.searchText ?? '') === searchText) return;

    this.model.set({ ...model, searchText });
    this._update();
  }

}
